import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { CircleUserRound, ImageOff, MoreHorizontal } from "lucide-react";
import { LoadImage } from "../../../../components/Loaders/LoadImage";
import { TypeUsersProp } from "@/pages/Users";
import { ShowBtn } from "@/components/Show";
import { DeleteBtn } from "@/components/Delete";
import Grid from "@/components/Grid";
import { CreateLoaderInput } from "@/components/Loaders";
import { UpdateUserTenantBtn } from "../Update";
import {
  deleteUserTenantData,
  getShowUserTenant,
  setStatusUserTenant,
} from "@/Api/usersTenant";
import { ComponentSwitchToggleStatus } from "@/App/Courses/ActivedOrDisabled";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";

export const ComponentTableUsers = ({
  dataApi,
  updateFront,
  pageActions,
  tenantUuid,
}) => {
  const { hasPermission } = usePermissions();
  return (
    <>
      <Table className="bg-zinc-50 rounded shadow dark:bg-[#2c2c2c]">
        <TableHeader>
          <TableRow>
            <TableHead>Foto</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Email</TableHead>
            <TableHead>Cargo</TableHead>
            <TableHead className="text-center">Ativo</TableHead>
            <TableHead className="text-center">Ações</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {dataApi?.data?.map((user: TypeUsersProp, index) => (
            <TableRow key={user?.id}>
              <TableCell>
                {user?.photo ? (
                  <LoadImage
                    src={user?.photo}
                    className="size-7 aspect-square object-cover rounded"
                  />
                ) : (
                  <CircleUserRound
                    strokeWidth={1.5}
                    className="text-slate-700 size-7 dark:text-[#F4F4F5]"
                  />
                )}
              </TableCell>
              <TableCell>{user?.name}</TableCell>
              <TableCell>{user?.email}</TableCell>
              <TableCell>{user?.roles?.map((role) => role?.name).join(", ")}</TableCell>
              <TableCell>
                <ComponentSwitchToggleStatus
                  defaultChecked={user?.is_active ? true : false}
                  params={{
                    func: setStatusUserTenant,
                    funcParams: {
                      tenant_uuid: tenantUuid,
                      user_uuid: user?.uuid,
                    },
                  }}
                  updateFront={() =>
                    updateFront((prev) => {
                      return {
                        ...prev,
                        data: prev?.data?.map((values) => {
                          if (values?.uuid === user?.uuid)
                            return {
                              ...values,
                              is_active: values?.is_active ? 0 : 1,
                            };

                          return values;
                        }),
                      };
                    })
                  }
                />
              </TableCell>
              <TableCell className="flex justify-center">
                <div className="flex gap-3 w-full justify-center">
                  <If condition={hasPermission("edit-user")}>
                    <UpdateUserTenantBtn
                      uuid={user?.uuid}
                      tenantUuid={tenantUuid}
                      updateFront={(response) => {
                        updateFront((prev) => {
                          prev?.data?.splice(index, 1, response);
                          return {
                            ...prev,
                            data: [...prev.data],
                          };
                        });
                      }}
                    />
                  </If>
                  <If condition={hasPermission("delete-user")}>
                    <DeleteBtn
                      params={{
                        func: deleteUserTenantData,
                        funcParams: {
                          tenant_uuid: tenantUuid,
                          user_uuid: user?.uuid,
                        },
                      }}
                      updateFront={() =>
                        updateFront((prev) => ({
                          ...prev,
                          data: [
                            ...prev?.data?.filter(
                              (data) => data?.uuid !== user?.uuid
                            ),
                          ],
                        }))
                      }
                      referenceLabel={user?.name}
                    />
                  </If>
                  <If condition={hasPermission("view-roles-list")}>
                    <ShowBtn
                      labels={{ title: "Visualizar Usuário" }}
                      params={{
                        func: getShowUserTenant,
                        funcParams: {
                          tenant_uuid: tenantUuid,
                          user_uuid: user?.uuid,
                        },
                      }}
                      render={(responseApi: TypeUsersProp) => (
                        <div className="w-full h-auto">
                          <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                            <div className="flex flex-col items-center justify-between">
                              {responseApi?.photo ? (
                                <LoadImage
                                  src={responseApi?.photo}
                                  className="aspect-square object-cover size-36 rounded self-start"
                                  iconSize={50}
                                />
                              ) : (
                                <ImageOff className="size-16 text-stone-300 flex-1" />
                              )}
                            </div>
                            <div className="flex flex-col gap-5">
                              <Grid.Item name="Nome" flex={0}>
                                {responseApi?.name}
                              </Grid.Item>
                              <Grid.Item name="E-mail" flex={0}>
                                {responseApi?.email}
                              </Grid.Item>
                              <Grid.Item name="Cargo" flex={0}>
                                {responseApi?.roles?.map((role) => role?.name).join(", ")}
                              </Grid.Item>
                            </div>
                          </div>
                        </div>
                      )}
                      loader={<CreateLoaderInput lines={4} />}
                    />
                  </If>
                </div>
              </TableCell>
            </TableRow>
          ))}
          {dataApi?.data?.length < 1 && (
            <TableRow>
              <TableCell colSpan={5}>Nada foi encontrado.</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <div className="flex justify-between mb-3">
        {dataApi?.data?.length > 0 ? (
          <span className="font-medium">
            Exibindo {dataApi?.current_page} de um total de {dataApi.last_page}
          </span>
        ) : (
          <span className="font-medium">Exibindo 0 de um total de 0</span>
        )}
        {dataApi?.last_page > 1 && (
          <Pagination className="">
            <PaginationContent>
              <PaginationItem>
                <PaginationPrevious
                  href="#"
                  className="bg-red-700 hover:bg-red-800 h-8"
                  onClick={pageActions.previous}
                />
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="size-8 bg-transparent border-2 border-red-700 text-slate-900 font-bold cursor-auto hover:bg-transparent"
                >
                  <span className="pointer-events-none">
                    {dataApi.current_page}
                  </span>
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationLink
                  href="#"
                  className="text-black h-8 hover:bg-stone-950/10"
                  onClick={pageActions.lastPage}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </PaginationLink>
              </PaginationItem>
              <PaginationItem>
                <PaginationNext
                  href="#"
                  className="bg-red-700 hover:bg-red-800 h-8"
                  onClick={pageActions.next}
                />
              </PaginationItem>
            </PaginationContent>
          </Pagination>
        )}
      </div>
    </>
  );
};
